import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import StatusBadge from './common/StatusBadge';

const CardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  margin-bottom: 1rem;
  width: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
    border-color: var(--color-sky, #84AFFB);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 20px;
    flex-wrap: wrap;
    gap: 1rem;
  }
`;

const MainSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex: 1;
  min-width: 0;

  @media (max-width: 600px) {
    gap: 1rem;
  }
`;

const DateCircle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 70px;
  height: 70px;
  background: #f3f4f6;
  color: #111827;
  border-radius: 18px;
  padding: 0.5rem;

  .day {
    font-size: 1.25rem;
    font-weight: 800;
    line-height: 1;
  }
  .month {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    margin-top: 2px;
  }

  @media (max-width: 600px) {
    min-width: 60px;
    height: 60px;
    .day { font-size: 1.1rem; }
    .month { font-size: 0.65rem; }
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;

  .duration {
    color: #6b7280;
    font-weight: 500;
    font-size: 0.875rem;
  }
`;

const Title = styled.h3`
  font-size: 1.125rem;
  font-weight: 800;
  color: #111827;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  flex-wrap: wrap;

  .tag {
    background: #f3f4f6;
    color: #4b5563;
    padding: 0.1rem 0.5rem;
    border-radius: 6px;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
  }
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-top: 0.75rem;
    border-top: 1px solid #f3f4f6;
  }
`;

const PriceText = styled.div`
  font-size: 1.125rem;
  font-weight: 800;
  color: #111827;
`;

const BookButton = styled.button`
  background: var(--color-coral, #FF6648);
  color: white;
  padding: 0.6rem 1.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 102, 72, 0.2);

  &:hover {
    background: #e55b40;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(255, 102, 72, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

interface AdvancedLessonCardProps {
  lesson: {
    id: string | number;
    dateTime: string;
    duration: number;
    location?: string;
    pickupLocation?: string;
    status?: string;
    cost?: number;
    currency?: string;
    type?: string;
    studentName?: string;
    teacherName?: string;
  };
  mode?: 'available' | 'booked' | 'passed';
  onBook?: (id: string | number) => void | Promise<void>;
  isBooking?: boolean;
  onClick?: (id: string | number) => void;
  showPrice?: boolean;
  showTeacherName?: boolean;
}

const AdvancedLessonCard: React.FC<AdvancedLessonCardProps> = ({ 
  lesson, 
  mode = 'booked', 
  onBook, 
  isBooking, 
  onClick, 
  showPrice,
  showTeacherName 
}) => {
  const { t, i18n } = useTranslation();

  const dt = new Date(lesson.dateTime);
  
  // Format components
  const day = dt.getDate();
  const month = dt.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-GB', { month: 'short' });
  const timeStr = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  
  const displayName = showTeacherName ? lesson.teacherName : lesson.studentName;
  const isExam = lesson.type?.toUpperCase() === 'EXAM';

  const formatCurrency = (amount?: number, currency: string = 'EUR') => {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat(i18n.language === 'nl' ? 'nl-NL' : 'en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const handleBook = (e: React.MouseEvent) => {
    e.stopPropagation();
    onBook?.(lesson.id);
  };

  return (
    <CardContainer onClick={() => onClick?.(lesson.id)}>
      <MainSection>
        <DateCircle>
          <span className="day">{day}</span>
          <span className="month">{month}</span>
        </DateCircle>
        
        <InfoContent>
          <TimeRow>
            {timeStr}
            <span className="duration">({lesson.duration} min)</span>
          </TimeRow>
          
          <Title>
            {isExam && <span style={{ color: 'var(--color-vibrant-blue)', marginRight: '0.5rem', fontWeight: 700 }}>EXAM</span>}
            {displayName || ''}
          </Title>
          
          <MetaRow>
            {lesson.type && <span className="tag">{t(`portal.lesson.type.${lesson.type.toLowerCase()}`, lesson.type)}</span>}
            <span>{lesson.pickupLocation || lesson.location || '—'}</span>
          </MetaRow>
        </InfoContent>
      </MainSection>

      <RightSection>
        {showPrice && lesson.cost !== undefined && (
          <PriceText>{formatCurrency(lesson.cost, lesson.currency)}</PriceText>
        )}
        
        {mode === 'available' && onBook ? (
          <BookButton onClick={handleBook} disabled={isBooking}>
            {isBooking ? '...' : t('portal.common.book', 'Book')}
          </BookButton>
        ) : (
          <StatusBadge status={lesson.status}>
            {t(`portal.lesson.status.${(lesson.status || 'scheduled').toLowerCase()}`, lesson.status || 'Scheduled')}
          </StatusBadge>
        )}
      </RightSection>
    </CardContainer>
  );
};

export default AdvancedLessonCard;
